import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/** _define.scss の `pc` と同じ（768px 以上のみ SimpleBar） */
const PC_MQ = "(min-width: 768px)";
/** SP 帯（767px 以下）では SimpleBar を一切使わない */
const SP_MQ = "(max-width: 767px)";

let simpleBarInstance = null;

/** リロード時に .main（PC）／window（SP）の scrollTop を戻す */
const SCROLL_RESTORE_SS_KEY = "anshin_sozoku_main_scroll_restore_v1";
let scrollRestoreListenerAttached = false;

function getScrollRestorePathKey() {
  return window.location.pathname + window.location.search;
}

function saveMainScrollForRestore() {
  try {
    const pc = isPcViewport();
    let scrollTop = 0;
    if (pc) {
      const el = getPcMainScrollElement();
      scrollTop = el ? Math.round(el.scrollTop) : 0;
    } else {
      scrollTop = Math.round(
        window.scrollY || document.documentElement.scrollTop || 0
      );
    }
    sessionStorage.setItem(
      SCROLL_RESTORE_SS_KEY,
      JSON.stringify({
        v: 1,
        path: getScrollRestorePathKey(),
        isPc: pc,
        scrollTop,
      })
    );
  } catch (e) {
    /* quota / private mode */
  }
}

function clearStoredMainScrollRestore() {
  try {
    sessionStorage.removeItem(SCROLL_RESTORE_SS_KEY);
  } catch (e) {
    /* ignore */
  }
}

function attachMainScrollRestoreListeners() {
  if (scrollRestoreListenerAttached) return;
  scrollRestoreListenerAttached = true;
  window.addEventListener("pagehide", saveMainScrollForRestore);
}

/**
 * 直前の pagehide で保存した値があれば復元（同一 path・同一 PC/SP のときのみ）。
 * sessionStorage は検証後すぐに消費し、二重適用を防ぐ。
 */
function tryRestoreMainScrollFromStorage() {
  let data;
  try {
    const raw = sessionStorage.getItem(SCROLL_RESTORE_SS_KEY);
    if (!raw) return;
    data = JSON.parse(raw);
  } catch (e) {
    clearStoredMainScrollRestore();
    return;
  }
  if (!data || data.v !== 1 || typeof data.path !== "string") {
    clearStoredMainScrollRestore();
    return;
  }
  if (data.path !== getScrollRestorePathKey()) {
    clearStoredMainScrollRestore();
    return;
  }
  if (Boolean(data.isPc) !== isPcViewport()) {
    clearStoredMainScrollRestore();
    return;
  }

  clearStoredMainScrollRestore();

  const top = Math.max(0, Number(data.scrollTop) || 0);
  const savedIsPc = Boolean(data.isPc);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (savedIsPc !== isPcViewport()) return;
      if (isPcViewport()) {
        const el = getPcMainScrollElement();
        if (el) el.scrollTop = top;
      } else {
        window.scrollTo(0, top);
      }
      try {
        ScrollTrigger.refresh();
      } catch (e) {
        /* ignore */
      }
    });
  });
}

function isPcViewport() {
  return window.matchMedia(PC_MQ).matches;
}

function setSimpleBarHtmlClass(active) {
  document.documentElement.classList.toggle("has-main-simplebar", Boolean(active));
}

/**
 * SimpleBar の unMount はリスナー解除のみで DOM を戻さないため、
 * PC→SP 後に残った .simplebar-* を外して元の子要素だけに戻す。
 */
function unwrapSimpleBarFromMain(main) {
  if (!main) return;
  const content = main.querySelector(".simplebar-content");
  if (!content) {
    main.removeAttribute("data-simplebar");
    return;
  }
  const fragment = document.createDocumentFragment();
  while (content.firstChild) {
    fragment.appendChild(content.firstChild);
  }
  while (main.firstChild) {
    main.removeChild(main.firstChild);
  }
  main.appendChild(fragment);
  main.removeAttribute("data-simplebar");
}

/** SP 時・リサイズ時: インスタンスを外し、WeakMap に残った参照も掃除する */
function teardownSimpleBar(main) {
  if (simpleBarInstance) {
    try {
      simpleBarInstance.unMount();
    } catch (e) {
      /* ignore */
    }
    simpleBarInstance = null;
  }
  if (main && SimpleBar.instances && typeof SimpleBar.instances.get === "function") {
    const leaked = SimpleBar.instances.get(main);
    if (leaked) {
      try {
        leaked.unMount();
      } catch (e) {
        /* ignore */
      }
      try {
        SimpleBar.instances.delete(main);
      } catch (e) {
        /* ignore */
      }
    }
  }
  unwrapSimpleBarFromMain(main);
  setSimpleBarHtmlClass(false);
}

/**
 * PC 時: SimpleBar の実スクロール要素。
 * SP 時: 常に null（ウィンドウスクロール。SimpleBar は無効）。
 */
export function getPcMainScrollElement() {
  if (!isPcViewport()) {
    return null;
  }
  if (simpleBarInstance && typeof simpleBarInstance.getScrollElement === "function") {
    const el = simpleBarInstance.getScrollElement();
    if (el) return el;
  }
  if (simpleBarInstance && simpleBarInstance.contentWrapperEl) {
    return simpleBarInstance.contentWrapperEl;
  }
  /* PC 初期化直前など、SimpleBar 未生成時は従来どおり .main をスクロール先にする */
  return document.querySelector(".main");
}

/**
 * 768px 以上のときだけ .main に SimpleBar。767px 以下（SP）では必ず teardown。
 * @param {() => void} [onAfterSync] スクロール先の変更後にリスナーを張り直す用
 */
export function initMainSimpleBar(onAfterSync) {
  const main = document.querySelector(".main");
  if (!main) return;

  attachMainScrollRestoreListeners();

  const mqPc = window.matchMedia(PC_MQ);
  const mqSp = window.matchMedia(SP_MQ);

  const sync = () => {
    const useSimpleBar = mqPc.matches && !mqSp.matches;

    if (useSimpleBar) {
      if (!simpleBarInstance) {
        simpleBarInstance = new SimpleBar(main, {
          autoHide: false,
        });
      }
      setSimpleBarHtmlClass(true);
    } else {
      teardownSimpleBar(main);
    }
    ScrollTrigger.refresh();
    if (typeof onAfterSync === "function") {
      onAfterSync();
    }
    tryRestoreMainScrollFromStorage();
  };

  sync();
  mqPc.addEventListener("change", sync);
  mqSp.addEventListener("change", sync);
}
